import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { FulfillmentStepKind, OrderStatus } from "client/api-v2";
import type { OrderDto, OrderFulfillmentDto, OrderFulfillmentStepDto } from "client/api-v2";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { AcceptOnBehalfDialog } from "areas/orders/components/AcceptOnBehalfDialog";
import {
  OrderActionPreventedByUnsavedChangesDialog,
  disabledLookSx,
} from "areas/orders/components/OrderLifecycleActions";
import { formatInstantInUtc } from "areas/orders/components/OrderVersionHistory";
import { RetryBillingAction } from "areas/orders/components/RetryBillingAction";
import { orderToasts } from "areas/orders/components/chrome/orderToasts";
import { completeFulfilmentStep, reopenFulfilmentStep } from "areas/orders/services/fulfilmentService";
import { describeFulfilmentStatus } from "areas/orders/utils/fulfilmentStatus";
import { describeOrderError } from "areas/orders/utils/orderErrors";

const stepLabels: Record<FulfillmentStepKind, string> = {
  [FulfillmentStepKind.OrderAccepted]: "Order accepted",
  [FulfillmentStepKind.BillingOrderCreated]: "Billing order created",
  [FulfillmentStepKind.ProvisioningDateReached]: "Provisioning date reached",
  [FulfillmentStepKind.ManualSignOff]: "Manual sign-off",
  [FulfillmentStepKind.SubscriptionModified]: "Subscription modified",
};

const pipelineKinds = [
  FulfillmentStepKind.OrderAccepted,
  FulfillmentStepKind.BillingOrderCreated,
  FulfillmentStepKind.SubscriptionModified,
] as const;

export const fulfilmentUnavailableFallback = "Fulfillment tracking is not available yet.";

export const fulfilmentReadPendingMessage = "Reading the fulfillment record.";

export const fulfilmentReadPending: OrderFulfillmentDto = {
  isAvailable: false,
  unavailableReason: fulfilmentReadPendingMessage,
  fulfillmentId: null,
  processName: null,
  processVersion: null,
  earliestProvisioningDate: null,
  provisionedAt: null,
  steps: [],
};

export function fulfilmentReadFailed(reason: string): OrderFulfillmentDto {
  return { ...fulfilmentReadPending, unavailableReason: `The fulfillment record could not be read. ${reason}` };
}

export const resetReasonRequiredMessage = "Give a reason for undoing this approval.";

export const notRequiredCompletionWarning =
  "This step is not required for this order, so completing it releases nothing. The record keeps the completion and names you.";

export const fulfilmentWriteForbiddenMessage = `You need the ${RbacPolicies.Fulfillments.ReadWrite} role to act on a fulfillment step.`;

const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;

const automaticModeLabel = "Automatic";
const manualModeLabel = "Manual approval";
const inProgressMeta = "In progress";
const notRequiredMeta = "Not required";
const gateHeldMeta = "Held — approval required";
const heldByGateMeta = "Held";
const gateNotAppliedMeta = "Not applied";
const provisioningNotRaisedMeta = "Not raised";
const provisioningOutstandingMeta = "Outstanding";
const provisionedMeta = "Provisioned";

type StepTone = "done" | "current" | "pending";

interface PipelineRow {
  kind: FulfillmentStepKind;
  tone: StepTone;
  meta: string;
}

interface FlagView {
  meta: string;
  note: string | null;
  colour: "success.main" | "warning.main" | "info.main" | "text.secondary";
  isDotFilled: boolean;
  isEmphasised: boolean;
}

interface FlagAction {
  label: string;
  isBusy: boolean;
  onAct: () => void;
  forbiddenReason?: string;
}

function indexSteps(steps: OrderFulfillmentStepDto[]): Map<FulfillmentStepKind, OrderFulfillmentStepDto> {
  return new Map(steps.map((step) => [step.kind, step]));
}

function isGateHeld(gate?: OrderFulfillmentStepDto): boolean {
  return gate !== undefined && gate.isRequired && !gate.isComplete;
}

function buildPipeline(steps: Map<FulfillmentStepKind, OrderFulfillmentStepDto>): PipelineRow[] {
  const rows = pipelineKinds.map((kind) => steps.get(kind)).filter((step) => step !== undefined);
  const currentIndex = rows.findIndex((step) => step.isRequired && !step.isComplete);
  const heldByGate = isGateHeld(steps.get(FulfillmentStepKind.ManualSignOff));

  return rows.map((step, index) => {
    const isHeld = heldByGate && step.kind === FulfillmentStepKind.SubscriptionModified && !step.isComplete;
    const isCurrent = index === currentIndex && !isHeld;

    return {
      kind: step.kind,
      tone: step.isComplete ? "done" : isCurrent ? "current" : "pending",
      meta: describeStepProgress(step, isCurrent, isHeld),
    };
  });
}

function describeStepProgress(step: OrderFulfillmentStepDto, isCurrent: boolean, isHeld: boolean): string {
  if (step.isComplete) return step.completedAt ? formatInstantInUtc(step.completedAt) : "";
  if (!step.isRequired) return notRequiredMeta;
  if (isHeld) return heldByGateMeta;
  return isCurrent ? inProgressMeta : "";
}

function actorOf(step?: OrderFulfillmentStepDto): string | null {
  const actor = step?.lastModifiedByUser.trim();
  return actor === undefined || actor === "" ? null : actor;
}

function describeStepNote(step: OrderFulfillmentStepDto | undefined, actor: string | null): string | null {
  const note = step?.details?.trim();
  if (note === undefined || note === "") return null;
  return step?.isComplete || actor === null ? note : `${note} — ${actor}`;
}

function describeGate(step?: OrderFulfillmentStepDto): FlagView {
  const actor = actorOf(step);
  const note = describeStepNote(step, actor);

  if (step?.isComplete) {
    const cleared = step.completedAt ? `Cleared ${formatInstantInUtc(step.completedAt)}` : "Cleared";
    return {
      meta: actor === null ? cleared : `${cleared} by ${actor}`,
      note,
      colour: "success.main",
      isDotFilled: true,
      isEmphasised: false,
    };
  }
  if (step?.isRequired) {
    return { meta: gateHeldMeta, note, colour: "warning.main", isDotFilled: false, isEmphasised: true };
  }
  return { meta: gateNotAppliedMeta, note, colour: "text.secondary", isDotFilled: false, isEmphasised: false };
}

function describeProvisioning(
  step: OrderFulfillmentStepDto | undefined,
  earliestProvisioningDate: string | null
): FlagView {
  const earliest = earliestProvisioningDate
    ? ` (from ${formatDateOnly(getOrParseDateTime(earliestProvisioningDate))})`
    : "";
  const note = describeStepNote(step, null);

  if (step?.isComplete) {
    const done = step.completedAt
      ? `${provisionedMeta} ${formatDateOnly(getOrParseDateTime(step.completedAt))}`
      : provisionedMeta;
    return { meta: `${done}${earliest}`, note, colour: "success.main", isDotFilled: true, isEmphasised: false };
  }
  if (step && !step.isRequired) {
    return {
      meta: `${notRequiredMeta}${earliest}`,
      note,
      colour: "text.secondary",
      isDotFilled: false,
      isEmphasised: false,
    };
  }
  if (!earliestProvisioningDate) {
    return { meta: provisioningNotRaisedMeta, note, colour: "text.secondary", isDotFilled: false, isEmphasised: false };
  }
  return {
    meta: `${provisioningOutstandingMeta}${earliest}`,
    note,
    colour: "info.main",
    isDotFilled: false,
    isEmphasised: true,
  };
}

function PipelineStep({ row, isLast, action }: { row: PipelineRow; isLast: boolean; action?: React.ReactNode }) {
  const isDone = row.tone === "done";
  const dotColour = isDone ? "success.main" : row.tone === "current" ? "info.main" : "divider";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: action ? "12px minmax(0, 1fr) auto" : "12px minmax(0, 1fr)",
        columnGap: 1.25,
      }}
    >
      <Stack sx={{ alignItems: "center" }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            mt: 0.375,
            flex: "0 0 auto",
            borderRadius: "50%",
            border: 1,
            borderColor: dotColour,
            bgcolor: isDone ? "success.main" : "transparent",
          }}
        />
        {!isLast && <Box sx={{ flex: 1, width: "2px", minHeight: 12, bgcolor: isDone ? "success.main" : "divider" }} />}
      </Stack>
      <Box sx={{ pb: isLast ? 0 : 1.25 }}>
        <Typography
          variant="body2"
          color={row.tone === "pending" ? "text.secondary" : "text.primary"}
          sx={{ fontWeight: row.tone === "current" ? 600 : 400 }}
        >
          {stepLabels[row.kind]}
        </Typography>
        {row.meta && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {row.meta}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

function FulfilmentFlag({
  label,
  view,
  action,
  onReset,
}: {
  label: string;
  view: FlagView;
  action?: FlagAction;
  onReset?: () => void;
}) {
  return (
    <Box
      sx={{ display: "grid", gridTemplateColumns: "12px minmax(0, 1fr) auto", columnGap: 1.25, alignItems: "center" }}
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          mt: 0.375,
          alignSelf: "start",
          borderRadius: "50%",
          border: 1,
          borderColor: view.colour,
          bgcolor: view.isDotFilled ? view.colour : "transparent",
        }}
      />
      <Box sx={{ alignSelf: "start" }}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: "baseline" }}>
          <Typography variant="body2" sx={{ color: view.colour, fontWeight: view.isEmphasised ? 600 : 400 }}>
            {label}
          </Typography>
          {onReset && (
            <Link component="button" type="button" variant="caption" onClick={onReset}>
              Reset
            </Link>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {view.meta}
        </Typography>
        {view.note && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>
            {view.note}
          </Typography>
        )}
      </Box>
      {action && <FlagActionButton action={action} />}
    </Box>
  );
}

function FlagActionButton({ action }: { action: FlagAction }) {
  const reasonId = React.useId();
  const button = (
    <Button
      variant="outlined"
      size="small"
      disabled={!!action.forbiddenReason}
      loading={action.isBusy}
      aria-describedby={action.forbiddenReason ? reasonId : undefined}
      onClick={action.onAct}
    >
      {action.label}
    </Button>
  );

  return action.forbiddenReason ? (
    <Tooltip title={action.forbiddenReason}>
      <span>
        {button}
        <Box component="span" id={reasonId} sx={visuallyHidden}>
          {action.forbiddenReason}
        </Box>
      </span>
    </Tooltip>
  ) : (
    button
  );
}

export function FulfilmentPanel({
  order,
  fulfillment,
  onFulfillmentChanged,
  canAcceptOnBehalf,
  canAct,
  onOrderChanged,
  isDirty,
}: {
  order: OrderDto;
  fulfillment?: OrderFulfillmentDto;
  onFulfillmentChanged: () => void;
  canAcceptOnBehalf: boolean;
  canAct: boolean;
  onOrderChanged: (order: OrderDto) => void;
  isDirty: boolean;
}) {
  const { sendNewAlert } = useAlerts();
  const { isAuthorized } = useRbacContext();
  const [error, setError] = useState<string | undefined>(undefined);
  const [busyStep, setBusyStep] = useState<FulfillmentStepKind | undefined>(undefined);
  const [isResetPrompted, setIsResetPrompted] = useState(false);
  const [resetReason, setResetReason] = useState("");
  const [isReasonMissing, setIsReasonMissing] = useState(false);
  const [resetError, setResetError] = useState<string | undefined>(undefined);
  const [isApprovePrompted, setIsApprovePrompted] = useState(false);
  const [approveError, setApproveError] = useState<string | undefined>(undefined);
  const [isAcceptOnBehalfOpen, setIsAcceptOnBehalfOpen] = useState(false);
  const [isAcceptBlockedBySave, setIsAcceptBlockedBySave] = useState(false);

  const fulfillmentId = fulfillment?.isAvailable ? fulfillment.fulfillmentId : null;
  const isReadPending = fulfillment?.unavailableReason === fulfilmentReadPendingMessage;
  const steps = indexSteps(fulfillment?.steps ?? []);
  const pipeline = buildPipeline(steps);
  const manualGate = steps.get(FulfillmentStepKind.ManualSignOff);
  const provisioningDate = steps.get(FulfillmentStepKind.ProvisioningDateReached);
  const forbiddenReason = isAuthorized(RbacPolicies.Fulfillments.ReadWrite)
    ? undefined
    : fulfilmentWriteForbiddenMessage;

  const complete = async (step: FulfillmentStepKind, toast: string) => {
    if (!fulfillmentId) return;
    setError(undefined);
    setBusyStep(step);
    try {
      await completeFulfilmentStep(fulfillmentId, step);
      onFulfillmentChanged();
      sendNewAlert(toast, AlertSeverity.Success);
    } catch (completionError) {
      setError(describeOrderError(completionError));
    } finally {
      setBusyStep(undefined);
    }
  };

  const completeFromPrompt = async (
    step: FulfillmentStepKind,
    toast: string,
    details: string | undefined,
    report: (message?: string) => void,
    close: () => void
  ) => {
    if (!fulfillmentId) return;
    report(undefined);
    try {
      await completeFulfilmentStep(fulfillmentId, step, details);
      close();
      onFulfillmentChanged();
      sendNewAlert(toast, AlertSeverity.Success);
    } catch (completionError) {
      report(describeOrderError(completionError));
    }
  };

  const promptApprove = () => {
    setApproveError(undefined);
    setIsApprovePrompted(true);
  };

  const approveGate = () =>
    completeFromPrompt(
      FulfillmentStepKind.ManualSignOff,
      orderToasts.fulfilmentApproved,
      undefined,
      setApproveError,
      () => setIsApprovePrompted(false)
    );

  const promptReset = () => {
    setResetReason("");
    setIsReasonMissing(false);
    setResetError(undefined);
    setIsResetPrompted(true);
  };

  const confirmReset = async () => {
    const reason = resetReason.trim();
    if (!fulfillmentId) return;
    if (reason === "") {
      setIsReasonMissing(true);
      return;
    }
    setIsReasonMissing(false);
    setResetError(undefined);
    try {
      await reopenFulfilmentStep(fulfillmentId, FulfillmentStepKind.ManualSignOff, reason);
      setIsResetPrompted(false);
      onFulfillmentChanged();
      sendNewAlert(orderToasts.manualGateReapplied, AlertSeverity.Success);
    } catch (reopenError) {
      setResetError(describeOrderError(reopenError));
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "baseline", justifyContent: "space-between" }}>
          <Typography variant="h6" component="h2">
            Fulfillment
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {order.requiresManualFulfillmentApproval ? manualModeLabel : automaticModeLabel}
          </Typography>
        </Stack>

        {fulfillmentId ? (
          <>
            <Box>
              {pipeline.map((row, index) => (
                <PipelineStep
                  key={row.kind}
                  row={row}
                  isLast={index === pipeline.length - 1}
                  action={
                    row.kind === FulfillmentStepKind.BillingOrderCreated &&
                    canAct &&
                    order.status === OrderStatus.Accepted ? (
                      <RetryBillingAction odNumber={order.odNumber} />
                    ) : undefined
                  }
                />
              ))}
            </Box>

            <Stack spacing={0.75}>
              <FulfilmentFlag
                label={stepLabels[FulfillmentStepKind.ManualSignOff]}
                view={describeGate(manualGate)}
                action={
                  manualGate?.canComplete
                    ? {
                        label: "Approve fulfillment",
                        isBusy: busyStep === FulfillmentStepKind.ManualSignOff,
                        onAct: isGateHeld(manualGate)
                          ? () => complete(FulfillmentStepKind.ManualSignOff, orderToasts.fulfilmentApproved)
                          : promptApprove,
                        forbiddenReason,
                      }
                    : undefined
                }
                onReset={manualGate?.canReopen && !forbiddenReason ? promptReset : undefined}
              />
              <FulfilmentFlag
                label={stepLabels[FulfillmentStepKind.ProvisioningDateReached]}
                view={describeProvisioning(provisioningDate, fulfillment?.earliestProvisioningDate ?? null)}
              />
            </Stack>

            {!!error && <Alert severity="error">{error}</Alert>}
          </>
        ) : isReadPending ? (
          <PendingRead />
        ) : (
          <UnreadableRecord
            order={order}
            fulfillment={fulfillment}
            onAcceptOnBehalf={
              canAcceptOnBehalf
                ? () => (isDirty ? setIsAcceptBlockedBySave(true) : setIsAcceptOnBehalfOpen(true))
                : undefined
            }
            isDirty={isDirty}
          />
        )}
      </Stack>

      <ActionDialog
        title="Approve a step this order does not need?"
        description={
          <>
            <Alert severity="warning">
              <Typography variant="body2">{notRequiredCompletionWarning}</Typography>
            </Alert>
            {!!approveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {approveError}
              </Alert>
            )}
          </>
        }
        show={isApprovePrompted}
        confirmText="Approve fulfillment"
        confirmColor="primary"
        onConfirm={approveGate}
        onCancel={() => setIsApprovePrompted(false)}
      />

      <ActionDialog
        title="Reapply the manual sign-off?"
        description={
          <>
            This undoes the fulfillment approval for {order.billingAccountLegalName}, so the sign-off holds the
            subscription change again. The reason is kept on the record.
            <TextField
              required
              fullWidth
              multiline
              minRows={2}
              label="Reason"
              value={resetReason}
              onChange={(event) => setResetReason(event.target.value)}
              error={isReasonMissing}
              helperText={isReasonMissing ? resetReasonRequiredMessage : undefined}
              sx={{ mt: 2 }}
            />
            {!!resetError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {resetError}
              </Alert>
            )}
          </>
        }
        show={isResetPrompted}
        confirmText="Reapply sign-off"
        onConfirm={confirmReset}
        onCancel={() => setIsResetPrompted(false)}
      />

      <OrderActionPreventedByUnsavedChangesDialog
        open={isAcceptBlockedBySave}
        onDismiss={() => setIsAcceptBlockedBySave(false)}
      />

      <AcceptOnBehalfDialog
        order={order}
        open={isAcceptOnBehalfOpen}
        onClose={() => setIsAcceptOnBehalfOpen(false)}
        onAccepted={(accepted) => {
          onOrderChanged(accepted);
          setIsAcceptOnBehalfOpen(false);
          sendNewAlert(orderToasts.accepted, AlertSeverity.Success);
        }}
      />
    </Paper>
  );
}

function PendingRead() {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <CircularProgress size={16} />
      <Typography variant="body2" color="text.secondary">
        {fulfilmentReadPendingMessage}
      </Typography>
    </Stack>
  );
}

function UnreadableRecord({
  order,
  fulfillment,
  onAcceptOnBehalf,
  isDirty,
}: {
  order: OrderDto;
  fulfillment?: OrderFulfillmentDto;
  onAcceptOnBehalf?: () => void;
  isDirty: boolean;
}) {
  const { headline, detail } = describeFulfilmentStatus(order);

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="body1">{headline}</Typography>
        {onAcceptOnBehalf && (
          <Button
            size="small"
            variant="outlined"
            sx={isDirty ? (theme) => disabledLookSx(theme, "outlined") : undefined}
            onClick={onAcceptOnBehalf}
          >
            Accept on behalf
          </Button>
        )}
      </Stack>
      {detail && (
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      )}
      <Alert severity="info">{fulfillment?.unavailableReason ?? fulfilmentUnavailableFallback}</Alert>
    </Stack>
  );
}
